import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/common";
import { CheckCircle2, XCircle, Loader2, ArrowLeft } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      if (attempts.current >= 6) { setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* keep trying */ }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  const icon = state === "paid" ? CheckCircle2 : state === "checking" ? Loader2 : XCircle;
  const Icon = icon;
  const title = { checking: "Confirming your payment…", paid: "Payment successful", timeout: "Still processing", error: "Payment issue" }[state];
  const msg = {
    checking: "Hang tight while we confirm with Stripe.",
    paid: "Your plan is now active. Thanks for upgrading AutoStore AI!",
    timeout: "Your payment is taking a moment to confirm. It will update shortly — check Billing.",
    error: "We couldn't confirm this payment. No plan change was made. Please try again.",
  }[state];

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#090B0E] p-6">
      <Card className="p-8 max-w-md w-full text-center" data-testid="payment-success-card">
        <div className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center ${state === "paid" ? "bg-emerald-500/15 text-emerald-400" : state === "checking" ? "bg-slate-800 text-slate-300" : "bg-red-500/15 text-red-400"}`}>
          <Icon className={`w-8 h-8 ${state === "checking" ? "animate-spin" : ""}`} />
        </div>
        <h1 className="font-head text-2xl font-bold text-slate-100 mt-5" data-testid="payment-status-title">{title}</h1>
        <p className="text-slate-400 mt-2 text-sm">{msg}</p>
        <Link to="/billing" data-testid="back-to-billing" className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500 text-slate-950 font-semibold hover:bg-emerald-400 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Billing
        </Link>
      </Card>
    </div>
  );
}
